// Everything this phone remembers. There are no accounts, so this is how people get back in.
import type { PayerDetails } from './api.ts';
import { ls } from './dom.ts';

export interface SavedSplit { code: string; venue: string; date: string; createdAt: string }
export interface Membership { id: string; token: string }
/** After the code is made: show it, tap your own items, then watch everyone pay. */
export type Stage = 'share' | 'mine' | 'collect';

const EMPTY_PROFILE: PayerDetails = { name: '', venmo: '', cashapp: '', zelle: '' };

export const saved = {
  /** The payer's name and payment handles, filled in once. */
  profile: (): PayerDetails => ({ ...EMPTY_PROFILE, ...ls.get<Partial<PayerDetails>>('profile', {}) }),
  setProfile: (p: PayerDetails): void => ls.set('profile', p),

  /** Whether this payer tips on the food or on the total with tax. Remembered for next time. */
  tipOn: (): 'food' | 'total' => (ls.get<string>('tipOn', 'food') === 'total' ? 'total' : 'food'),
  setTipOn: (on: 'food' | 'total'): void => ls.set('tipOn', on),

  /** Splits this phone created, newest first. */
  splits: (): SavedSplit[] => ls.get<SavedSplit[]>('splits', []),
  rememberSplit(s: SavedSplit): void {
    const list = saved.splits().filter((x) => x.code !== s.code);
    list.unshift({ code: s.code, venue: s.venue, date: s.date, createdAt: s.createdAt });
    ls.set('splits', list.slice(0, 20));
  },

  /** The private key that unlocks the payer's dashboard. */
  adminKey: (code: string): string | null => ls.get<string | null>('admin:' + code, null),
  setAdminKey: (code: string, key: string): void => ls.set('admin:' + code, key),

  /** Who this phone is in a split. */
  membership: (code: string): Membership | null => ls.get<Membership | null>('person:' + code, null),
  setMembership: (code: string, m: Membership): void => ls.set('person:' + code, m),

  /** This phone's number, if its owner typed it once, to fill in next time. */
  /** A friend's own name, kept on this phone so the next dinner's join box is filled in. */
  name: (): string => ls.get<string>('myName', ''),
  setName: (n: string): void => { const v = n.trim().slice(0, 40); if (v) ls.set('myName', v); },
  phone: (): string => ls.get<string>('myPhone', ''),
  setPhone: (p: string): void => { if (p.replace(/\D/g, '').length >= 7) ls.set('myPhone', p); },

  /** Whether this phone said yes to texts from this split's restaurant. */
  optedIn: (code: string): boolean => ls.get<boolean>('optin:' + code, false),
  setOptedIn: (code: string, on: boolean): void => ls.set('optin:' + code, on),

  /** Whether to show the "save your dashboard link" card. */
  isFresh: (code: string): boolean => ls.get<boolean>('fresh:' + code, false),
  setFresh: (code: string, fresh: boolean): void => ls.set('fresh:' + code, fresh),

  /** Which step of an open split the payer is on, so a reload lands back there. */
  stage: (code: string): Stage | null => ls.get<Stage | null>('stage:' + code, null),
  setStage: (code: string, stage: Stage): void => ls.set('stage:' + code, stage),
};
